import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, Tooltip, Menu, MenuItem, ListItemText, ListItemIcon } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import Tab from './Tab';
import { formatTabLabel, getCollidingTabDisambiguators } from './tabLabels';
import { useRovingTabIndex } from '../../hooks/useRovingTabIndex';

/**
 * TabBar Component
 * 
 * Chrome-style tab bar for multi-page navigation
 * Supports keyboard navigation:
 * - Left/Right arrows: Navigate between tabs
 * - Home/End: Jump to first/last tab
 * - Enter/Space: Activate focused tab
 */
function TabBar({ 
  tabs, 
  activeTabId, 
  onTabClick, 
  onTabClose, 
  onNewTab,
  onTabReorder,
  maxTabs = 10,
  className = ''
}) {
  const disambiguators = useMemo(() => getCollidingTabDisambiguators(tabs), [tabs]);

  // Find the index of the active tab for initial focus
  const activeIndex = tabs.findIndex(t => t.id === activeTabId);
  
  const handleTabSelect = useCallback((index) => {
    if (tabs[index]) {
      onTabClick(tabs[index].id);
    }
  }, [tabs, onTabClick]);

  const {
    focusedIndex,
    setFocusedIndex,
    getTabIndex,
    handleKeyDown,
    setItemRef,
  } = useRovingTabIndex({
    itemCount: tabs.length,
    initialIndex: activeIndex >= 0 ? activeIndex : 0,
    vertical: false,
    loop: true,
    onSelect: handleTabSelect,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

  // Sync focused index with active tab when it changes externally
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex !== focusedIndex) {
      setFocusedIndex(activeIndex);
    }
  }, [activeIndex]);

  const handleDragStart = (e, tabId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('tabId', tabId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetTabId) => {
    e.preventDefault();
    const draggedTabId = e.dataTransfer.getData('tabId');
    
    if (draggedTabId !== targetTabId) {
      const targetIndex = tabs.findIndex(t => t.id === targetTabId);
      if (targetIndex !== -1 && onTabReorder) {
        onTabReorder(draggedTabId, targetIndex);
      }
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll, tabs.length]);

  const scroll = useCallback((dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  }, []);

  // Overflow dropdown menu state
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const showOverflow = tabs.length > 5;

  return (
    <div 
      className={`flex items-center bg-gray-50 dark:bg-gray-800/90 border-b border-gray-200 dark:border-gray-700 shadow-[0_-1px_3px_rgba(0,0,0,0.05)] px-1.5 py-1 ${className}`}
      role="tablist"
      aria-label="Open tabs"
    >
      {canScrollLeft && (
        <IconButton size="small" onClick={() => scroll(-1)} aria-label="Scroll tabs left" className="flex-shrink-0">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      <div 
        ref={scrollRef}
        className="flex items-center flex-1 min-w-0 overflow-x-auto scrollbar-hide"
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.id}
            tab={tab}
            disambiguator={disambiguators.get(tab.id)}
            active={tab.id === activeTabId}
            onClick={() => onTabClick(tab.id)}
            onClose={() => onTabClose(tab.id)}
            tabIndex={getTabIndex(index)}
            ref={setItemRef(index)}
          />
        ))}
      </div>
      {canScrollRight && (
        <IconButton size="small" onClick={() => scroll(1)} aria-label="Scroll tabs right" className="flex-shrink-0">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}

      {showOverflow && (
        <>
          <Tooltip title="All tabs">
            <IconButton
              size="small"
              onClick={(e) => setOverflowAnchor(e.currentTarget)}
              aria-label="Show all tabs"
              className="flex-shrink-0"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={overflowAnchor}
            open={Boolean(overflowAnchor)}
            onClose={() => setOverflowAnchor(null)}
            slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 220 } } }}
          >
            {tabs.map((tab) => (
              <MenuItem
                key={tab.id}
                selected={tab.id === activeTabId}
                onClick={() => { onTabClick(tab.id); setOverflowAnchor(null); }}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={tab.title ? formatTabLabel(tab.title, disambiguators.get(tab.id)) : (tab.path || 'New Tab')}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }}
                />
                <ListItemIcon sx={{ minWidth: 'unset', ml: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onTabClose(tab.id); setOverflowAnchor(null); }}
                    aria-label={`Close ${tab.title ? formatTabLabel(tab.title, disambiguators.get(tab.id)) : 'tab'}`}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </ListItemIcon>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      
      <Tooltip title={tabs.length >= maxTabs ? 'Maximum tabs open — close a tab to open a new one' : 'New tab'}>
        <span>
          <IconButton
            onClick={onNewTab}
            disabled={tabs.length >= maxTabs}
            size="small"
            className="mx-2"
            aria-label={tabs.length >= maxTabs ? 'Maximum tabs open — close a tab to open a new one' : 'New tab'}
          >
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}

export default TabBar;
